import React, { useState, useEffect } from "react";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useVideoCVApi } from "src/_common/hooks/actions/videoCV/appVideoCVApiHook";
import ViewProfileModal from "../job-details/_common/ViewProfileModal";
import VideoCVFilters from "./VideoCVFilters";
import ProgressIndicator from "../job-details/_common/ProgressIndicator";
import { toast } from "react-toastify";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";

interface Jobseeker {
  jobseeker: {
    id: number;
    email?: string;
    mobile_number?: string;
  };
  document_full_path: {
    profile_pic: string;
    video_file: string;
    resume_file?: string;
  };
  basic_details: {
    full_name: string;
    cv_headline: string;
    key_skills_custome: string[];
    current_location: string;
    profile_summary?: string;
  };
  employment_details?: any[];
  education_details?: any[];
  certification_details?: any[];
  course_details?: any[];
  jobseeker_public_profiles?: any[];
  country_details?: {
    id: number;
    name: string;
  };
}

interface Pagination {
  returned_count: number;
  total: number;
  current_page: number;
  total_pages: number;
  per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

const VideoCVMain: React.FC = () => {
  const { t } = useTranslation("video-cv");
  const { callGetJobseekersWithVideo } = useVideoCVApi();

  // State variables
  const [jobseekers, setJobseekers] = useState<Jobseeker[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [isViewProfileOpen, setIsViewProfileOpen] = useState(false);
  const [selectedJobseeker, setSelectedJobseeker] = useState<any>(null);
  const [playingVideoId, setPlayingVideoId] = useState<number | null>(null);
  const [videoDurations, setVideoDurations] = useState<{ [key: number]: string }>({});
  const [videoMuted, setVideoMuted] = useState<{ [key: number]: boolean }>({});
  const itemsPerPage = 12;
  const toast = useToaster();

  const [sortBy, setSortBy] = useState<string>("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false); // Track if filters have been applied
  const [appliedFilters, setAppliedFilters] = useState<{
    keywords: string;
    location: string;
    country_id: string;
    city_id: string;
    skills: string;
  }>({
    keywords: "",
    location: "",
    country_id: "",
    city_id: "",
    skills: "",
  });

  // Fetch jobseekers only when filters have been applied
  useEffect(() => {
    if (hasSearched) {
      fetchJobseekers();
    }
  }, [sortBy, currentPage, appliedFilters, hasSearched]);

  const fetchJobseekers = () => {
    const params: any = {
      page: currentPage,
      per_page: itemsPerPage,
      sort_order: sortBy === "newest" ? "desc" : "asc",
    };

    // Add filters if they exist
    if (appliedFilters.keywords) {
      params.keyword = appliedFilters.keywords;
    }
    if (appliedFilters.skills) {
      params.skills = appliedFilters.skills;
    }
    if (appliedFilters.country_id) {
      params.country = appliedFilters.country_id;
    }
    if (appliedFilters.city_id) {
      params.city = appliedFilters.city_id;
    }

    setLoading(true);
    callGetJobseekersWithVideo(
      params,
      (message: string, data: any) => {
        setLoading(false);
        setJobseekers(data.jobseekers || []);
        setPagination(data.pagination || null);
        console.log("Jobseekers fetched successfully:", data);
      },
      (error: string) => {
        setLoading(false);
        setJobseekers([]);
        setPagination(null);
        console.error("Error fetching jobseekers:", error);
        toast.error(error);
      },
    );
  };

  const handleApplyFilters = (filters: {
    keywords: string;
    location: string;
    country_id: string;
    city_id: string;
    skills: string;
  }) => {
    setAppliedFilters(filters);
    setCurrentPage(1);
    setHasSearched(true); // Mark that search has been initiated
  };

  const handleResetFilters = () => {
    setAppliedFilters({
      keywords: "",
      location: "",
      country_id: "",
      city_id: "",
      skills: "",
    });
    setCurrentPage(1);
    setSortBy("newest");
    setHasSearched(false); // Reset search state
    setJobseekers([]); // Clear the list
    setPagination(null);
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleLoadedMetadata = (jobseekerId: number, event: React.SyntheticEvent<HTMLVideoElement>) => {
    const videoElement = event.currentTarget;
    const duration = videoElement.duration;
    if (!isNaN(duration)) {
      setVideoDurations((prev) => ({
        ...prev,
        [jobseekerId]: formatDuration(duration),
      }));
    }
    // Initialize muted state
    setVideoMuted((prev) => ({
      ...prev,
      [jobseekerId]: videoElement.muted,
    }));
  };

  const handlePlayVideo = (jobseekerId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    const videoElement = document.getElementById(`video-${jobseekerId}`) as HTMLVideoElement;

    if (videoElement) {
      if (playingVideoId === jobseekerId) {
        // Pause the video (keep current time)
        videoElement.pause();
        setPlayingVideoId(null);
      } else {
        // Pause any currently playing video and reset it
        if (playingVideoId !== null) {
          const currentVideo = document.getElementById(`video-${playingVideoId}`) as HTMLVideoElement;
          if (currentVideo) {
            currentVideo.pause();
            currentVideo.currentTime = 0;
          }
        }
        // Play the new video
        videoElement.play();
        setPlayingVideoId(jobseekerId);
      }
    }
  };

  const handleToggleMute = (jobseekerId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    const videoElement = document.getElementById(`video-${jobseekerId}`) as HTMLVideoElement;
    if (videoElement) {
      videoElement.muted = !videoElement.muted;
      setVideoMuted((prev) => ({
        ...prev,
        [jobseekerId]: videoElement.muted,
      }));
    }
  };

  const handleViewProfile = (jobseeker: any) => {
    console.log("Original jobseeker data:", jobseeker);

    // The API returns data in this structure based on your example:
    // - jobseeker.basic_details is an ARRAY with one object
    // - jobseeker.jobseeker contains account info
    // - jobseeker.education_details, employment_details, etc. are arrays

    const basicDetails = jobseeker.basic_details?.[0] || jobseeker.basic_details || {};
    const jobseekerAccount = jobseeker.jobseeker || {};

    // Extract gender information
    const getGenderLabel = (genderId: number) => {
      const genderMap: { [key: number]: string } = {
        1: "Male",
        2: "Female",
        3: "Other",
      };
      return genderMap[genderId] || "-";
    };

    // Format skills from key_skills_custome or key_skills array
    const skills = basicDetails.key_skills_custome || basicDetails.key_skills || [];
    const skillsString = Array.isArray(skills) ? skills.join(", ") : skills;

    // Format languages
    const languages = basicDetails.known_languages || "-";

    // Format the jobseeker data to match the ViewProfileModal's expected structure
    const formattedData = {
      // Top-level fields that ViewProfileModal expects (from your API data structure)
      id: jobseeker.id || jobseekerAccount.id,
      jobseekerId: jobseeker.jobseekerId || jobseekerAccount.id,
      name: jobseeker.name || basicDetails.full_name || basicDetails.name,
      avatar: jobseeker.avatar || jobseeker.document_full_path?.profile_pic,
      position: jobseeker.position || basicDetails.cv_headline || basicDetails.current_designation || "-",
      resume_file: jobseeker.system_resume_file || jobseeker.document_full_path?.system_resume_file,
      video_file: jobseeker.video_file || jobseeker.document_full_path?.video_file,

      // Profile header section fields
      currentDesignation:
        basicDetails.cv_headline ||
        jobseeker?.employment_details[0]?.designation ||
        // basicDetails.current_designation ||
        "",
      currentLocation: basicDetails.current_location || "",
      gender: getGenderLabel(basicDetails.gender_id),
      totalExperienceYear: basicDetails.total_experience_year || null,
      currentSalary: basicDetails.current_salary || null,
      currentSalaryCurrency: basicDetails.current_salary_currency || null,

      // Skills and profile summary
      skills: skillsString,
      profileSummary: basicDetails.profile_summary || "",

      // Personal details
      dateOfBirth: basicDetails.dob || basicDetails.date_of_birth || "",
      languages: languages,

      // Basic details object (for buttons and other info in modal)
      basicDetails: {
        jobseeker_id: jobseekerAccount.id,
        name: basicDetails.full_name || basicDetails.name,
        first_name: basicDetails.first_name || "",
        last_name: basicDetails.last_name || "",
        email: jobseekerAccount.email || jobseekerAccount.deleted_email || "",
        mobile_number: jobseekerAccount.mobile_number || jobseekerAccount.deleted_mobile_number || "",
        current_designation: basicDetails.cv_headline || basicDetails.current_designation,
        current_location: basicDetails.current_location || "",
        key_skills: skills,
        profile_summary: basicDetails.profile_summary || "",
        resume_file: jobseeker.document_full_path?.resume_file,
        video_file: jobseeker.document_full_path?.video_file,
        preferred_country: basicDetails.preferred_country || [],
        driving_license: basicDetails.driving_license || false,
        preferred_work_type: basicDetails.preferred_work_type || "",
        dob: basicDetails.dob || basicDetails.date_of_birth || "",
        gender_id: basicDetails.gender_id,
        known_languages: basicDetails.known_languages || "",
      },

      // Jobseeker account info (for secondary contact in personal details section)
      jobseekerAccount: {
        secondary_email: jobseekerAccount.secondary_email || "",
        secondary_mobile_number: jobseekerAccount.secondary_mobile_number || "",
        secondary_country_code: jobseekerAccount.secondary_country_code || "",
      },

      // Employment/Experience details (from employment_details or experience array)
      employmentDetails: jobseeker.employment_details || jobseeker.experience || [],

      // Education details (from education_details, education, or course_details)
      educationDetails: jobseeker.education_details || jobseeker.education || jobseeker.course_details || [],

      // Course details (separate from education if available)
      courseDetails: jobseeker.course_details || [],

      // Project details (from project_details or projects array)
      projectDetails: jobseeker.project_details || jobseeker.projects || [],

      // Certifications (from certification_details or certifications)
      certificateDetails: jobseeker.certification_details || jobseeker.certifications || [],

      // Document full paths
      document_full_path: jobseeker.document_full_path || {},

      // Public profile information
      jobseeker_public_profiles: jobseeker.jobseeker_public_profiles || [],
    };

    console.log("Formatted candidate details for modal:", formattedData);
    setSelectedJobseeker(formattedData);
    setIsViewProfileOpen(true);
  };

  // Calculate pagination display - show only 5 pages (current + 2 left + 2 right)
  const totalPages = pagination?.total_pages || 0;
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is less than or equal to 5
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate start and end based on current page
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);

      // Adjust if we're near the beginning
      if (currentPage <= 3) {
        startPage = 1;
        endPage = maxPagesToShow;
      }
      // Adjust if we're near the end
      else if (currentPage >= totalPages - 2) {
        startPage = totalPages - maxPagesToShow + 1;
        endPage = totalPages;
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  const pages = getPageNumbers();
  const showLeftDots = pages.length > 0 && pages[0] > 1;
  const showRightDots = pages.length > 0 && pages[pages.length - 1] < totalPages;

  return (
    <div className="dashboard-main member-page-wrapper">
      <div className="video-cv-container">
        <div className="video-cv-content">
          {/* Filters Sidebar */}
          <VideoCVFilters onApplyFilters={handleApplyFilters} onResetFilters={handleResetFilters} context="videoCv" />

          {/* Main Content */}
          <div className="video-cv-main">
            {!hasSearched ? (
              // Show ProgressIndicator when no filters have been applied
              <ProgressIndicator stage="video-cv" />
            ) : loading ? (
              <div className="loading-container">
                <p>{t("videoCv.candidates.loading", "Loading...")}</p>
              </div>
            ) : jobseekers.length > 0 ? (
              <>
                <div className="results-header">
                  <div className="results-info">
                    <h2>{t("videoCv.candidates.title", "Candidates")}</h2>
                    <p className="results-count">
                      {t("videoCv.candidates.showing", "Showing")} {pagination?.returned_count || 0}{" "}
                      {t("videoCv.candidates.results", "results")}
                      {/* {pagination && `of ${pagination.total}`} */}
                    </p>
                  </div>
                  <div className="sort-controls">
                    <label>{t("videoCv.candidates.sortBy", "Sort by:")}</label>
                    <select value={sortBy} onChange={(e) => handleSortChange(e.target.value)} className="sort-select">
                      {/* <option value="relevance">{t("videoCv.candidates.relevance", "Relevance")}</option> */}
                      <option value="newest">{t("videoCv.candidates.newest", "Newest")}</option>
                      <option value="oldest">{t("videoCv.candidates.oldest", "Oldest")}</option>
                      {/* <option value="experience">{t("videoCv.candidates.experience", "Experience")}</option> */}
                    </select>
                  </div>
                </div>

                {
                  <div className="candidates-grid">
                    {jobseekers.map((jobseeker: Jobseeker) => (
                      <div key={jobseeker.jobseeker.id} className="candidate-card">
                        <div className="video-container">
                          <div className="video-placeholder">
                            <video
                              id={`video-${jobseeker.jobseeker.id}`}
                              src={jobseeker?.document_full_path?.video_file}
                              className="candidate-image"
                              controls={false}
                              onLoadedMetadata={(e) => handleLoadedMetadata(jobseeker.jobseeker.id, e)}
                              onEnded={() => setPlayingVideoId(null)}
                              onClick={(e) =>
                                playingVideoId === jobseeker.jobseeker.id && handlePlayVideo(jobseeker.jobseeker.id, e)
                              }
                            />
                            {/* Play/Pause button overlay */}
                            <div className="video-overlay">
                              {playingVideoId === jobseeker.jobseeker.id ? (
                                <button
                                  className="play-button pause-button"
                                  onClick={(e) => handlePlayVideo(jobseeker.jobseeker.id, e)}
                                >
                                  <i className="fas fa-pause"></i>
                                </button>
                              ) : (
                                <button
                                  className="play-button"
                                  onClick={(e) => handlePlayVideo(jobseeker.jobseeker.id, e)}
                                >
                                  <i className="fas fa-play"></i>
                                </button>
                              )}

                              <div className="video-controls">
                                <div className="duration">{videoDurations[jobseeker.jobseeker.id] || "0:00"}</div>
                                <button
                                  className="volume-button"
                                  onClick={(e) => handleToggleMute(jobseeker.jobseeker.id, e)}
                                >
                                  <i
                                    className={`fas ${
                                      videoMuted[jobseeker.jobseeker.id] ? "fa-volume-mute" : "fa-volume-up"
                                    }`}
                                  ></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="candidate-info">
                          <div className="candidate-header">
                            <img
                              src={jobseeker.document_full_path.profile_pic || "/images/placeholder.jpg"}
                              alt={jobseeker.basic_details.full_name}
                              className="candidate-avatar"
                              onError={(e) => {
                                e.currentTarget.src = "/images/placeholder.jpg";
                              }}
                            />
                            <div className="candidate-details">
                              <h3 className="candidate-name">{jobseeker?.basic_details?.full_name}</h3>
                              <p className="candidate-position">
                                {jobseeker?.basic_details?.cv_headline ||
                                  jobseeker?.employment_details?.[0]?.designation}
                              </p>
                            </div>
                          </div>

                          {/* <div className="candidate-skills">
                        {jobseeker?.basic_details?.key_skills_custome
                          ?.slice(0, 3)
                          .map((skill: string, index: number) => (
                            <span key={index} className="skill-tag">
                              {skill}
                            </span>
                          ))}
                      </div> */}

                          {jobseeker?.country_details?.name && (
                            <div className="candidate-meta">
                              <div className="meta-item">
                                <i className="fa fa-map-marker"></i>
                                <span>{jobseeker?.country_details?.name}</span>
                              </div>
                            </div>
                          )}

                          <div className="candidate-actions">
                            <button className="view-profile-button" onClick={() => handleViewProfile(jobseeker)}>
                              {t("videoCv.candidates.viewProfile", "View Profile")}
                            </button>
                            {/* {jobseeker?.jobseeker?.email && (
                              <button
                                className="message-button"
                                onClick={() => (window.location.href = `mailto:${jobseeker.jobseeker.email}`)}
                              >
                                <i className="fa fa-envelope"></i>
                              </button>
                            )} */}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                }

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="page-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || loading}
                    >
                      {t("videoCv.pagination.prev", "Previous")}
                    </button>
                    <div className="page-numbers">
                      {/* Show dots on left if needed */}
                      {showLeftDots && <span className="dots">...</span>}

                      {/* Page Numbers - show only 5 pages */}
                      {pages.map((page) => (
                        <button
                          key={page}
                          className={`page-num ${currentPage === page ? "active" : ""}`}
                          onClick={() => handlePageChange(page)}
                          disabled={loading}
                        >
                          {page}
                        </button>
                      ))}

                      {/* Show dots on right if needed */}
                      {showRightDots && <span className="dots">...</span>}
                    </div>
                    <button
                      className="page-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || totalPages === 0 || loading}
                    >
                      {t("videoCv.pagination.next", "Next")}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <ProgressIndicator stage="no-video-cv" />
            )}
          </div>
        </div>
      </div>

      {/* View Profile Modal */}
      {isViewProfileOpen && selectedJobseeker && (
        <ViewProfileModal
          isOpen={isViewProfileOpen}
          onClose={() => setIsViewProfileOpen(false)}
          jobseekerId={selectedJobseeker.basicDetails?.jobseeker_id || selectedJobseeker.jobseekerId}
        />
      )}
    </div>
  );
};

export default VideoCVMain;
